import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const CATEGORIES = ['Vegetables', 'Fruits', 'Dairy', 'Grains', 'Snacks', 'Beverages', 'Organic', 'Other'];

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'Vegetables', price: '', stockQuantity: '', description: '', status: 'available' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const fileRef = useRef();

  const fetch = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.set('category', filter);
      if (search) params.set('search', search);
      const { data } = await api.get(`/products?${params}&limit=100`);
      setProducts(data.data.products);
      setCategories(data.data.categories || []);
    } catch { toast('Failed to load products', 'error'); }
    finally { setLoading(false); }
  }, [filter, search, toast]);

  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => { setEditing(null); setForm({ name: '', category: 'Vegetables', price: '', stockQuantity: '', description: '', status: 'available' }); setImageFile(null); setImagePreview(null); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, category: p.category, price: p.price, stockQuantity: p.stock_quantity, description: p.description || '', status: p.status }); setImageFile(null); setImagePreview(p.image_url); setShowModal(true); };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast('Name and price are required', 'error'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append('image', imageFile);

      if (editing) {
        await api.put(`/products/${editing.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast('Product updated!', 'success');
      } else {
        await api.post('/products', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast('Product created!', 'success');
      }
      setShowModal(false);
      fetch();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save product', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast('Product deleted', 'success');
      fetch();
    } catch { toast('Failed to delete', 'error'); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>🛒 Products</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{products.length} products</p>
        </div>
        <button id="add-product-btn" className="btn btn-primary" onClick={openAdd}>+ Add Product</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-bar" style={{ flex: '1 1 220px' }}>
          <span className="search-icon">🔍</span>
          <input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={`btn btn-sm ${!filter ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('')}>All</button>
          {categories.map(c => (
            <button key={c} className={`btn btn-sm ${filter === c ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(c)}>{c}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex-center" style={{ height: 200 }}><div className="spinner"></div></div>
      ) : products.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">🛒</div><p>No products yet</p><button className="btn btn-primary" onClick={openAdd}>+ Add Product</button></div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {p.image_url ? <img src={p.image_url} alt={p.name} className="product-img" /> : <div className="product-img-placeholder">🥬</div>}
                        <div>
                          <div style={{ fontWeight: 600 }}>{p.name}</div>
                          {p.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{p.description.slice(0, 50)}{p.description.length > 50 ? '...' : ''}</div>}
                        </div>
                      </div>
                    </td>
                    <td><span className="badge badge-packed" style={{ fontSize: 11 }}>{p.category}</span></td>
                    <td style={{ fontWeight: 700, fontSize: 15 }}>₹{parseFloat(p.price).toLocaleString('en-IN')}</td>
                    <td>
                      <div style={{ fontWeight: 600, color: p.stock_quantity <= 5 ? 'var(--danger)' : p.stock_quantity <= 20 ? 'var(--warning)' : 'var(--success)' }}>
                        {p.stock_quantity} units
                        {p.stock_quantity <= 5 && <span style={{ fontSize: 10, marginLeft: 6 }}>⚠️ Low</span>}
                      </div>
                    </td>
                    <td><span className={`badge badge-${p.status}`}>{p.status.replace('_', ' ')}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>✏️ Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editing ? 'Edit Product' : 'Add Product'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Product Name *</label>
                  <input className="form-input" placeholder="e.g. Fresh Tomatoes" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Price (₹) *</label>
                  <input className="form-input" type="number" step="0.01" placeholder="0.00" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Stock Quantity</label>
                  <input className="form-input" type="number" placeholder="0" value={form.stockQuantity} onChange={e => setForm(p => ({ ...p, stockQuantity: e.target.value }))} />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Description</label>
                <textarea className="form-textarea" placeholder="Product description..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Product Image</label>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} style={{ display: 'none' }} />
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {imagePreview && <img src={imagePreview} alt="Preview" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />}
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current.click()}>
                    {imagePreview ? '🔄 Change Image' : '📷 Upload Image'}
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update Product' : 'Create Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
